import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Star, CheckCircle, XCircle, Trophy, Sparkles } from 'lucide-react';

const CreditCardComparePage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ids = (params.get('ids') || '').split(',').filter(Boolean);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ids.length === 0) {
      toast.error('No cards selected to compare');
      navigate('/credit-cards');
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/credit-cards/compare?ids=${ids.join(',')}`);
        setCards(res.data || []);
      } catch (e) {
        toast.error('Failed to load comparison');
        navigate('/credit-cards');
      } finally {
        setLoading(false);
      }
    })();
  }, [params.get('ids')]); // eslint-disable-line

  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FF6B35' }}>Loading comparison...</div>;
  }
  if (cards.length === 0) return null;

  // Best-of helpers
  const min = (key) => Math.min(...cards.map((c) => Number(c[key] || Infinity)).filter((n) => !isNaN(n) && n !== Infinity));
  const max = (key) => Math.max(...cards.map((c) => Number(c[key] || -Infinity)).filter((n) => !isNaN(n) && n !== -Infinity));

  const rows = [
    { label: 'Bank', key: 'bank_name' },
    { label: 'Category', key: 'category', transform: (v) => String(v || '-').replace(/^./, (m) => m.toUpperCase()) },
    { label: 'Rating', key: 'rating', best: 'max', render: (v) => v > 0 ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Star size={12} fill="#F59E0B" stroke="#F59E0B" /> {v}</span> : '-' },
    { label: 'Joining Fee', key: 'joining_fee', best: 'min', render: (v) => v > 0 ? `₹${v.toLocaleString('en-IN')}` : 'FREE' },
    { label: 'Annual Fee', key: 'annual_fee', best: 'min', render: (v) => v > 0 ? `₹${v.toLocaleString('en-IN')}` : 'FREE' },
    { label: 'Annual Fee Waiver', key: 'annual_fee_waiver_at', render: (v) => v > 0 ? `On ₹${(v / 100000).toFixed(1)}L spend` : 'N/A' },
    { label: 'Reward Rate', key: 'reward_rate' },
    { label: 'Welcome Benefit', key: 'welcome_benefit' },
    { label: 'Min Annual Income', key: 'min_income', best: 'min', render: (v) => v > 0 ? `₹${v.toLocaleString('en-IN')}` : 'No minimum' },
    { label: 'Interest Rate', key: 'interest_rate' },
    { label: 'ATM Withdrawal Fee', key: 'atm_withdrawal_fee' },
    { label: 'Late Payment Fee', key: 'late_payment_fee' },
    { label: 'Forex Markup', key: 'forex_markup' },
    { label: 'Credit Limit Range', key: 'credit_limit_range' },
    { label: 'EMI Conversion', key: 'emi_conversion' },
    { label: 'Eligibility Age', key: 'eligibility_age' },
  ];

  const bestValue = (row) => {
    if (!row.best) return null;
    return row.best === 'min' ? min(row.key) : max(row.key);
  };

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <Navbar />
      <section style={{ background: 'linear-gradient(135deg,#FFF7ED 0%,#FFFFFF 100%)', padding: '4rem 1.5rem 1.5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <button onClick={() => navigate('/credit-cards')} style={backBtn} data-testid="ccc-back">
            <ArrowLeft size={16} /> Back to credit cards
          </button>
          <h1 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.5rem)', color: '#0F172A', margin: 0 }}>
            Compare <span className="gradient-text-primary">{cards.length} Credit Cards</span>
          </h1>
          <p style={{ color: '#475569', marginTop: '0.5rem' }}>Best values are highlighted with <Trophy size={14} style={{ display: 'inline', color: '#10B981', verticalAlign: 'middle' }} /></p>
        </div>
      </section>

      <section style={{ padding: '1.5rem 1.5rem 5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="calculator-section" style={{ padding: 0, overflowX: 'auto' }} data-testid="ccc-table">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                  <th style={{ ...th, position: 'sticky', left: 0, background: '#F8FAFC', zIndex: 2 }}>Feature</th>
                  {cards.map((c) => (
                    <th key={c.id} style={th}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '0.5rem 0' }}>
                        {c.bank_logo && <img src={c.bank_logo} alt={c.bank_name} style={{ width: 40, height: 40, borderRadius: 10, background: '#fff', padding: 4, border: '1px solid #E2E8F0', objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                        <div style={{ fontWeight: 800, color: '#0F172A', fontSize: '0.92rem', textAlign: 'center', lineHeight: 1.2 }}>{c.name}</div>
                        <div style={{ fontSize: '0.72rem', color: '#475569' }}>{c.bank_name}</div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const best = bestValue(row);
                  return (
                    <tr key={row.key} style={{ borderBottom: '1px solid #F1F5F9' }} data-testid={`ccc-row-${row.key}`}>
                      <td style={{ ...td, fontWeight: 700, color: '#0F172A', background: '#FAFAFA', position: 'sticky', left: 0, zIndex: 1 }}>{row.label}</td>
                      {cards.map((c) => {
                        const v = c[row.key];
                        const display = row.render ? row.render(v) : row.transform ? row.transform(v) : (v ?? '-');
                        const isBest = best !== null && Number(v) === best && v !== null && v !== undefined && v !== '';
                        return (
                          <td key={c.id} style={{ ...td, textAlign: 'center', background: isBest ? '#ECFDF5' : 'transparent', color: isBest ? '#065F46' : '#0F172A', fontWeight: isBest ? 700 : 400 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                              {isBest && <Trophy size={13} />} {display || '-'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

                <tr style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ ...td, fontWeight: 700, color: '#0F172A', background: '#FAFAFA', position: 'sticky', left: 0 }}>Key Features</td>
                  {cards.map((c) => (
                    <td key={c.id} style={{ ...td, verticalAlign: 'top' }}>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none', textAlign: 'left' }}>
                        {(c.key_features || []).slice(0, 4).map((f, i) => (
                          <li key={i} style={{ display: 'flex', gap: 6, fontSize: '0.78rem', color: '#475569', marginBottom: 4 }}>
                            <CheckCircle size={12} style={{ color: '#10B981', flexShrink: 0, marginTop: 3 }} /> {f}
                          </li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>

                <tr>
                  <td style={{ ...td, position: 'sticky', left: 0, background: '#fff' }}></td>
                  {cards.map((c) => (
                    <td key={c.id} style={{ ...td, textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button onClick={() => navigate(`/credit-cards/${c.id}`)} className="outline-button" style={{ fontSize: '0.78rem', padding: '0.4rem 0.6rem' }} data-testid={`ccc-detail-${c.id}`}>View Details</button>
                        <button onClick={() => navigate(`/enquiry?service=${encodeURIComponent(c.name)}`)} className="primary-button" style={{ fontSize: '0.78rem', padding: '0.4rem 0.6rem', justifyContent: 'center' }} data-testid={`ccc-apply-${c.id}`}>
                          Apply <ArrowRight size={12} />
                        </button>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

const th = { padding: '0.85rem 0.9rem', textAlign: 'left', fontSize: '0.78rem', color: '#0F172A' };
const td = { padding: '0.7rem 0.9rem', fontSize: '0.85rem', color: '#0F172A' };
const backBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '1.25rem', fontWeight: 600, fontSize: '0.9rem' };

export default CreditCardComparePage;
